import React, { useState, useEffect, useRef, useCallback } from 'react'

import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import { useTrackApiCalls } from '../../../utilities/useTrackApiCalls'
import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'
import { exportData, orderStatuses } from '../../../utilities/helpers'

import { getHolders } from '../../../utilities/api'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'

import { SearchBar } from '../../SearchBar'
import { ExportBtn } from '../../ExportBtn'
import { LoadingContainer } from '../../LoadingContainer'
import { Pagination } from '../../Pagination'
import Holder from '../Holder/Holder'

const HIDDEN_STATUSES = [
  'incomplete',
  'failed',
  'pending_send',
  'released',
  'refunded',
  'reserved',
]

// Unpaid is a filter-only bucket (open/reserved minus comps), not an order
// status, so it is listed here instead of in the shared orderStatuses map.
const statusOpts = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Unpaid',
    value: 'unpaid',
  },
  ...[
    ...new Map(
      Object.values(orderStatuses)
        .filter((obj) => !HIDDEN_STATUSES.includes(obj.value))
        .map((obj) => [obj.value, { label: obj.label, value: obj.value }])
    ).values(),
  ],
]

export default function HoldersList({
  seasonId,
  organizationName,
  seasonName,
}) {
  // to track if component has mounted once already
  const didMountRef = useRef(false)
  // to track if seasons have changed to reset state
  const isSeasonResetRef = useRef(false)

  const { incrementCallId, isLastCall } = useTrackApiCalls()

  // state to track if component has mounted once already
  const [isFirstTime, setIsFirstTime] = useState(true)

  // initial render loading
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)

  // set when searching - show spinner
  const [isLoading, setIsLoading] = useState(false)

  // search query
  const [query, setQuery] = useState('')

  const [status, setStatus] = useState(statusOpts[0].value)

  const [holders, setHolders] = useState([])

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(query, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime, q, callId, statusFilter = status) => {
      console.log('run search', isFirstTime, seasonId)
      if (isFirstTime) setIsLoadingInitial(true)
      if (!isLoading) setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          getHolders(
            {
              seasonId: Number(seasonId),
              query: q,
              page: currentPage + 1,
              perPage,
              status: statusFilter,
            },
            signal
          )
        )
        const {
          data,
          meta: {
            pagination: { pageCount: pc },
          },
        } = res.data

        // if on last api call set state
        if (isLastCall(callId)) {
          setHolders(data)
          setPageCount(pc)
          setIsLoading(false)
        }
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        if (isFirstTime) setIsLoadingInitial(false)
        if (isLastCall(callId)) {
          setIsFirstTime(isFirstTime)
          setIsLoading(false)
        }
      }
    },
    [
      abortable,
      currentPage,
      perPage,
      debouncedQuery,
      seasonId,
      status,
      setPageCount,
    ]
  )

  // run when currentPage or perPage changes and is mounted
  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render - to prevent duplicate runSearch calls
    callApi() // ✅ run only for real changes
  }, [currentPage, perPage])

  // run when query or status changes and currentPage is 0 and is mounted
  useEffect(() => {
    console.log(
      'check runSearch--- currentPage === 0 && didMountRef.current',
      currentPage,
      didMountRef.current,
      query,
      status,
      isSeasonResetRef.current
    )
    // Guard: If we are in the middle of a season reset, skip the API call flow
    if (isSeasonResetRef.current) {
      isSeasonResetRef.current = false
      return
    }

    if (currentPage === 0 && didMountRef.current) {
      callApi()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status])

  // Logic to reset query and status when seasonId changes
  useEffect(() => {
    // Skip on the very first mount
    if (!didMountRef.current) return

    // 1. Set the flag to temporarily suppress the filter effect
    isSeasonResetRef.current = true

    console.log('season change', query)
    // (1) no filters changed
    if (query === '' && status === 'all') isSeasonResetRef.current = false
    // (2) query changed, status is default
    else if (query && status === 'all') {
      console.log('reset query', query)
      setQuery('')
    }
    // (3) status changed, query is empty
    else if (!query && status !== 'all') {
      setStatus('all')
    }
    // (4) both changed
    else {
      setQuery('')
      setStatus('all')
    }
    setCurrentPage(0) // Resetting the page ensures a fresh search from the start

    callApi(true, '', 'all')

    console.log('Season changed, resetting filters and page.')
  }, [seasonId])

  useEffect(() => {
    callApi(true) // True as in it is initial query
    didMountRef.current = true
  }, [])

  const callApi = (
    isFirstTime = false,
    query = debouncedQuery,
    statusFilter = status
  ) => {
    const callId = incrementCallId() // Increment for each new call
    runSearch(isFirstTime, query, callId, statusFilter)
  }

  const handleExportHolders = async () => {
    try {
      if (!seasonId) return

      const perPageForExport = 500
      let page = 1
      let totalPages = 1
      const allHolders = []

      do {
        const res = await getHolders({
          seasonId: Number(seasonId),
          query: debouncedQuery,
          page,
          perPage: perPageForExport,
          status,
        })

        const responseData = res?.data?.data || []
        const pagination = res?.data?.meta?.pagination || {}
        totalPages = Number(pagination.pageCount || 1)

        allHolders.push(...responseData)
        page += 1
      } while (page <= totalPages)

      if (!allHolders.length) return

      exportData(
        allHolders.map((holder) => ({
          ...holder,
          season_name: seasonName,
        })),
        'season_holders',
        undefined,
        seasonName,
        undefined,
        organizationName
      )
    } catch (err) {
      console.error('Failed to export package holders', err)
    }
  }

  return (
    <>
      {isLoadingInitial ? (
        <LoadingContainer />
      ) : (
        <>
          <Card body className="card--md">
            <Header
              holders={holders}
              isFirstTime={isFirstTime}
              setQuery={setQuery}
              status={status}
              setStatus={setStatus}
              onExport={handleExportHolders}
            />
            <Results
              isLoading={isLoading}
              isFirstTime={isFirstTime}
              holders={holders}
            />
          </Card>
          {!isLoading && holders?.length > 0 && (
            <Pagination
              pageCount={pageCount}
              perPage={perPage}
              currentPage={currentPage}
              resultsPerPageOpts={resultsPerPageOpts}
              handleClick={(value) => setCurrentPage(value.selected)}
              handleChange={handleChange}
            />
          )}
        </>
      )}
    </>
  )
}

function Header({
  holders,
  isFirstTime,
  setQuery,
  status,
  setStatus,
  onExport,
}) {
  return (
    <>
      {(!isFirstTime || holders?.length > 0) && (
        <>
          <div className="section-header-sm section-heading section-heading--primary">
            <Card.Title as="h1" className="card-title-lg">
              Package Holders
            </Card.Title>
          </div>
          <Stack direction="horizontal" gap={3} className="mb-5">
            <div className="flex-grow-1">
              <SearchBar
                getQuery={setQuery}
                placeholder="Search by name, email, or phone number"
              />
            </div>
            <FloatingLabel controlId="holder-status" label="Status">
              <Form.Select
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statusOpts.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            <ExportBtn downloadReport={onExport} />
          </Stack>
        </>
      )}
    </>
  )
}

function Results({ isLoading, holders, isFirstTime }) {
  if (isLoading) {
    return <LoadingContainer />
  }

  // first time render and no holders - won't trigger when searching
  if (isFirstTime && !holders?.length) {
    return (
      <>
        <h1 className="fs-md text-center">No package holders.</h1>
        <p className="small text-center">
          Create a package and start selling tickets.
        </p>
      </>
    )
  }

  if (holders?.length === 0) {
    return (
      <>
        <h1 className="fs-md text-center">
          No package holders matching filters.
        </h1>
        <p className="small text-center">
          Please adjust your search filters and try again.
        </p>
      </>
    )
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>Seats</th>
          <th>Number of seats</th>
          <th>Number of tickets</th>
          <th>Package</th>
          <th>Name</th>
          <th>Email</th>
          <th>Phone number</th>
          <th>Address</th>
          <th>Purchase date</th>
          <th>Date paid</th>
          <th>Amount due</th>
          <th>Total</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {holders &&
          holders.map((holder, index) => (
            <tr key={index}>
              <Holder holder={holder} />
            </tr>
          ))}
      </tbody>
    </Table>
  )
}
